/**
 * The dock flash: a ring that snaps shut over a connection point.
 *
 * Confirming a connect on the shelf is otherwise silent - the ghost simply
 * stops being translucent. A one-shot ring collapsing onto the point gives
 * the moment the "click" it deserves. The ring is a DOM element driven by a
 * CSS animation, so no per-frame JavaScript runs; it removes itself when the
 * animation ends.
 */

import { useEffect } from "react";
import { Html } from "@react-three/drei";
import { useConnectSnap, useViewerStore } from "../store";

/** Comfortably past the CSS animation, which owns the actual timing. */
const CLEANUP_MS = 900;

export function ConnectSnapEffect() {
  const snap = useConnectSnap();
  const clearConnectSnap = useViewerStore((state) => state.clearConnectSnap);

  useEffect(() => {
    if (!snap) {
      return;
    }
    const timer = window.setTimeout(clearConnectSnap, CLEANUP_MS);
    return () => window.clearTimeout(timer);
  }, [snap, clearConnectSnap]);

  if (!snap) {
    return null;
  }

  return (
    <Html key={snap.seq} position={snap.position} center style={{ pointerEvents: "none" }}>
      <style>{`
        @keyframes connect-snap-ring {
          0%   { transform: scale(2.2); opacity: 0; }
          25%  { opacity: 1; }
          70%  { transform: scale(0.9); opacity: 0.9; }
          100% { transform: scale(1.15); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .connect-snap-ring { animation-duration: 1ms !important; }
        }
      `}</style>
      <div
        className="connect-snap-ring"
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          border: "4px solid #ff8c1a",
          boxShadow: "0 0 12px rgba(255, 140, 26, 0.8)",
          animation: "connect-snap-ring 0.6s cubic-bezier(0.2, 0.8, 0.3, 1) forwards",
        }}
      />
    </Html>
  );
}
